import React, { useRef, useState } from 'react';
import type { FreimeldungPublicItem } from '../../../services/freimeldungPublicService';
import { HEIC_ACCEPT } from '../../../services/imageService';
import {
  EINGABE,
  EINGABE_FEHLER,
  FEHLERKASTEN,
  HINWEISKASTEN,
  KNOPF_PRIMAER,
  KNOPF_SEKUNDAER,
  TEXTLINK,
} from '../../sifa/public/teilnahmeSeiteStil';
import {
  ANDERE_FIRMA,
  MAX_EMAIL,
  MAX_FIRMA,
  MAX_KOMMENTAR,
  MAX_NAME,
  MAX_PHONE,
  fristText,
  nummerText,
  type FreimeldungFeldFehler,
  type FreimeldungFormular as Formularwerte,
  type FreimeldungTexte,
  type MeldeArt,
  type Sprache,
} from './freimeldungLogic';

/**
 * Das Meldeformular zu EINEM Mangel: oben, worum es geht, darunter Firma,
 * Name, Kontakt, Kommentar und das Beweisfoto.
 *
 * Die Firma kommt als Auswahl aus den Zustaendigkeiten des Berichts — wer
 * nicht dabei ist, waehlt „Andere Firma …" und tippt sie ein. Das Foto wird
 * sofort hochgeladen (der Knopf zum Absenden bleibt so lange gesperrt), im
 * Formular steht danach nur noch die URL.
 *
 * Bei `art = 'zurueckgewiesen'` wird aus dem Kommentar die Begruendung
 * (Pflicht) und das Foto ist immer freiwillig — es belegt dann den
 * Widerspruch, nicht eine Erledigung.
 */
export const FreimeldungFormular: React.FC<{
  item: FreimeldungPublicItem;
  art: MeldeArt;
  formular: Formularwerte;
  firmen: string[];
  texte: FreimeldungTexte;
  sprache: Sprache;
  fotoPflicht: boolean;
  fotoLaedt: boolean;
  sendet: boolean;
  fehler: string | null;
  feldFehler: FreimeldungFeldFehler;
  onFeld: <K extends keyof Formularwerte>(feld: K, wert: Formularwerte[K]) => void;
  onFoto: (datei: File | null) => void;
  onFotoEntfernen: () => void;
  onAbsenden: () => void;
  onZurueck: () => void;
  /** Beschriftung „Auf Plan zeigen“; leer = der Mangel hat keinen freigegebenen Plan. */
  planKnopf?: string;
  onPlan?: () => void;
}> = ({
  item,
  art,
  formular,
  firmen,
  texte,
  sprache,
  fotoPflicht,
  fotoLaedt,
  sendet,
  fehler,
  feldFehler,
  onFeld,
  onFoto,
  onFotoEntfernen,
  onAbsenden,
  onZurueck,
  planKnopf,
  onPlan,
}) => {
  const dateiFeld = useRef<HTMLInputElement>(null);
  // Steht die Firma nicht in der Vorschlagsliste, ist das Freitextfeld offen —
  // auch nach „Weiteren Mangel melden", wo die Eingabe stehen bleibt.
  const [freitext, setFreitext] = useState(() => !!formular.firma && !firmen.includes(formular.firma));
  const nurFreitext = firmen.length === 0;
  const zeigeFreitext = freitext || nurFreitext;
  const nummer = nummerText(item);
  const gesperrt = sendet || fotoLaedt;
  const zurueck = art === 'zurueckgewiesen';
  const fotoNoetig = fotoPflicht && !zurueck;

  const absenden = (e: React.FormEvent) => {
    e.preventDefault();
    onAbsenden();
  };

  const firmaGewaehlt = (wert: string) => {
    if (wert === ANDERE_FIRMA) {
      setFreitext(true);
      onFeld('firma', '');
      return;
    }
    setFreitext(false);
    onFeld('firma', wert);
  };

  const feldId = (name: string) => `freimeldung-${name}`;

  const fehlerZeile = (name: keyof Formularwerte) => {
    const text = feldFehler[name];
    if (!text) return null;
    return (
      <p id={`${feldId(String(name))}-fehler`} className="text-sm text-red-600">
        {text}
      </p>
    );
  };

  const textfeld = (
    name: 'name' | 'email' | 'phone',
    label: string,
    typ: string,
    autoComplete: string,
    maxLength: number,
    inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'],
  ) => {
    const id = feldId(name);
    const text = feldFehler[name];
    return (
      <div className="space-y-1.5">
        <label htmlFor={id} className="block text-base font-semibold text-slate-700">
          {label}
        </label>
        <input
          id={id}
          name={name}
          type={typ}
          inputMode={inputMode}
          autoComplete={autoComplete}
          maxLength={maxLength}
          value={formular[name]}
          onChange={(e) => onFeld(name, e.target.value)}
          aria-invalid={text ? true : undefined}
          aria-describedby={text ? `${id}-fehler` : undefined}
          className={`${EINGABE} ${text ? EINGABE_FEHLER : ''}`}
        />
        {fehlerZeile(name)}
      </div>
    );
  };

  return (
    <form onSubmit={absenden} noValidate className="space-y-5">
      <div className="space-y-1">
        <h2 className="text-xl font-bold text-slate-900">{zurueck ? texte.formularTitelZurueck : texte.formularTitel}</h2>
        {zurueck && <p className="text-slate-600">{texte.zurueckHinweis}</p>}
      </div>

      <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          {nummer && <span className="px-2 py-0.5 rounded-lg bg-slate-800 text-white text-sm font-bold">{nummer}</span>}
          <span className="text-sm text-slate-500">
            {texte.frist}: {fristText(item, sprache)}
          </span>
        </div>
        <p className="font-semibold text-slate-900 break-words">{item.title}</p>
        {item.location && (
          <p className="text-sm text-slate-600 break-words">
            {texte.ort}: {item.location}
          </p>
        )}
        {planKnopf && onPlan && (
          <button type="button" onClick={onPlan} className={TEXTLINK}>
            {planKnopf}
          </button>
        )}
      </div>

      {/* Firma: Auswahl aus den Zustaendigkeiten, sonst Freitext. */}
      <div className="space-y-1.5">
        <label htmlFor={feldId(nurFreitext ? 'firma' : 'firma-auswahl')} className="block text-base font-semibold text-slate-700">
          {texte.labelFirma}
        </label>
        {!nurFreitext && (
          <select
            id={feldId('firma-auswahl')}
            name="firma-auswahl"
            value={zeigeFreitext ? ANDERE_FIRMA : formular.firma}
            onChange={(e) => firmaGewaehlt(e.target.value)}
            aria-invalid={feldFehler.firma ? true : undefined}
            className={`${EINGABE} ${feldFehler.firma ? EINGABE_FEHLER : ''}`}
          >
            <option value="">{texte.firmaBitteWaehlen}</option>
            {firmen.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
            <option value={ANDERE_FIRMA}>{texte.andereFirmaOption}</option>
          </select>
        )}
        {zeigeFreitext && (
          <input
            id={feldId('firma')}
            name="firma"
            type="text"
            autoComplete="organization"
            maxLength={MAX_FIRMA}
            placeholder={nurFreitext ? undefined : texte.labelAndereFirma}
            aria-label={texte.labelAndereFirma}
            value={formular.firma}
            onChange={(e) => onFeld('firma', e.target.value)}
            aria-invalid={feldFehler.firma ? true : undefined}
            aria-describedby={feldFehler.firma ? `${feldId('firma')}-fehler` : undefined}
            className={`${EINGABE} ${feldFehler.firma ? EINGABE_FEHLER : ''}`}
          />
        )}
        {fehlerZeile('firma')}
      </div>

      {textfeld('name', texte.labelName, 'text', 'name', MAX_NAME)}
      {textfeld('email', texte.labelEmail, 'email', 'email', MAX_EMAIL, 'email')}
      {textfeld('phone', texte.labelTelefon, 'tel', 'tel', MAX_PHONE, 'tel')}

      <div className="space-y-1.5">
        <label htmlFor={feldId('kommentar')} className="block text-base font-semibold text-slate-700">
          {zurueck ? texte.labelBegruendung : texte.labelKommentar}{' '}
          <span className={`font-normal ${zurueck ? 'text-red-600' : 'text-slate-500'}`}>
            ({zurueck ? texte.pflicht : texte.optional})
          </span>
        </label>
        <textarea
          id={feldId('kommentar')}
          name="kommentar"
          rows={zurueck ? 4 : 3}
          maxLength={MAX_KOMMENTAR}
          placeholder={zurueck ? texte.begruendungPlatzhalter : texte.kommentarPlatzhalter}
          value={formular.kommentar}
          onChange={(e) => onFeld('kommentar', e.target.value)}
          aria-invalid={feldFehler.kommentar ? true : undefined}
          aria-describedby={feldFehler.kommentar ? `${feldId('kommentar')}-fehler` : undefined}
          className={`${EINGABE} ${feldFehler.kommentar ? EINGABE_FEHLER : ''}`}
        />
        {fehlerZeile('kommentar')}
      </div>

      {/* Beweisfoto: pro Projekt einstellbar (metadata.freimeldung.fotoPflicht),
          bei einer Zurueckweisung immer freiwillig. */}
      <div className="space-y-2">
        <p className="text-base font-semibold text-slate-700">
          {texte.labelFoto}{' '}
          <span className={`font-normal ${fotoNoetig ? 'text-red-600' : 'text-slate-500'}`}>
            ({fotoNoetig ? texte.pflicht : texte.optional})
          </span>
        </p>

        {formular.fotoUrl && (
          <img
            src={formular.fotoUrl}
            alt=""
            className="w-32 h-32 rounded-xl object-cover border border-slate-200 bg-slate-100"
          />
        )}

        <input
          ref={dateiFeld}
          id={feldId('foto')}
          type="file"
          accept={`image/*,${HEIC_ACCEPT}`}
          // KEIN capture: mit `capture` oeffnet das Handy sofort die Kamera,
          // ein bereits am Vormittag aufgenommenes Foto aus der Mediathek
          // (und damit HEIC_ACCEPT) waere unerreichbar. Die Auswahl des
          // Betriebssystems bietet die Kamera weiterhin an.
          className="hidden"
          onChange={(e) => {
            const datei = e.target.files?.[0] ?? null;
            // Zuruecksetzen, damit dasselbe Foto nach einem Fehlschlag noch
            // einmal gewaehlt werden kann (der Browser meldet sonst kein change).
            e.target.value = '';
            onFoto(datei);
          }}
        />

        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={() => dateiFeld.current?.click()}
            disabled={gesperrt}
            className={KNOPF_SEKUNDAER}
          >
            {formular.fotoUrl ? texte.fotoErsetzen : texte.fotoWaehlen}
          </button>
          {formular.fotoUrl && !fotoLaedt && (
            <button type="button" onClick={onFotoEntfernen} disabled={sendet} className={KNOPF_SEKUNDAER}>
              {texte.fotoEntfernen}
            </button>
          )}
        </div>

        {fotoLaedt && (
          <p className="text-sm text-slate-600" aria-live="polite">
            {texte.fotoLaedt}
          </p>
        )}
        {!fotoLaedt && formular.fotoUrl && (
          <p className="text-sm text-emerald-700" aria-live="polite">
            {texte.fotoBereit}
          </p>
        )}
        {fehlerZeile('fotoUrl')}
      </div>

      {fehler && (
        <div className={FEHLERKASTEN} role="alert">
          {fehler}
        </div>
      )}

      <div className={HINWEISKASTEN}>{texte.hinweisPruefung}</div>

      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-between">
        <button type="button" onClick={onZurueck} disabled={sendet} className={KNOPF_SEKUNDAER}>
          {texte.zurueck}
        </button>
        <button type="submit" disabled={gesperrt} className={KNOPF_PRIMAER}>
          {sendet ? texte.sendet : zurueck ? texte.absendenZurueck : texte.absenden}
        </button>
      </div>
    </form>
  );
};
